import React, {useState} from 'react';

import {makeStyles} from 'tss-react/mui';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  WordCountArea,
} from 'web-library';
import {joiResolver} from '@hookform/resolvers/joi';
import {AxiosError} from 'axios';
import {QueryObserverResult, RefetchOptions} from 'react-query';
import {
  SendUniqueLinkEmailFormData,
  usePostSendUniqueLinkEmail,
} from '../../../api-refactored/unique-link/usePostSendUniqueLinkEmail';
import {SelectedUserInfo} from './model';
// import AdminSelectedSubjects from './AdminSelectedSubjects';
// import AdminSubjectListing from './AdminSubjectListing';
import createUniqueLinkFormSchema from './logic/createUniqueLinkFormSchema';
import {GenericErrorResponse} from '../../../api';
import {CoordinatorUniqueLinkCountResponse} from '../../../api-refactored/unique-link/useGetCoordinatorUniqueLinkCount';
import ControlledDatePicker from 'web-library/stories/atom/DatePicker/ControlledDatePicker';

const useStyles = makeStyles()((theme) => ({
  viewDetailsDialog: {
    '& .MuiPaper-root.MuiDialog-paper': {
      padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
      '& .MuiDialogTitle-root': {
        padding: `0 0 ${theme.spacing(2)} 0`,
      },
      '& .MuiDialogContent-root': {
        padding: `0 0 ${theme.spacing(3)} 0`,
      },
      '& .MuiDialogActions-root': {
        padding: 0,
      },
    },
  },
  input: {
    marginTop: theme.spacing(3),
  },
  dialogContentContainer: {
    marginLeft: theme.spacing(0.3),
  },
  selectContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    maxWidth: '960px',
  },
  rootContainer: {
    display: 'flex',
    alignItems: 'center',
    minHeight: theme.spacing(6),
  },
  arrowIconContainer: {
    right: '-10px',
  },
  chipsContainer: {
    overflowX: 'hidden',
    width: '100%',
  },
  iconButton: {
    position: 'absolute',
    right: '45px',
    top: 'calc(50% - 12px)',
    padding: 0,
  },
  chipLabel: {
    padding: 0,
  },
  divider: {
    paddingBottom: theme.spacing(4),
  },
  errorText: {
    textAlign: 'end',
    color: theme.color.state.stateError,
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

interface GrantAccessModalProps {
  accountType: string;
  examLevelUuid: string;
  userInfo: SelectedUserInfo;
  modalState: boolean;
  onDialogClose: () => void;
  updateEmailStatus: (index: number, status: string, customMessage?: string) => void;
  showWavieSubjectFee?: boolean;
  applicationType: string;
  isGCESelected: boolean;
  refetchCoordinatorListing?: (
      options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<CoordinatorUniqueLinkCountResponse[], AxiosError<GenericErrorResponse>>>;
  fetchCandidate?: () => void;
}

const GrantAccessModal = ({
                            accountType,
                            examLevelUuid,
                            userInfo,
                            modalState,
                            onDialogClose,
                            updateEmailStatus,
                            showWavieSubjectFee = true,
                            applicationType,
                            isGCESelected,
                            refetchCoordinatorListing,
                            fetchCandidate,
                          }: GrantAccessModalProps) => {
  const {t} = useTranslation();

  const {classes} = useStyles();

  const formHook = useForm<SendUniqueLinkEmailFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(createUniqueLinkFormSchema()),
  });
  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState: {errors},
  } = formHook;

  const {mutate: sendUniqueLinkEmailMutate, status: sendEmailStatus} = usePostSendUniqueLinkEmail(
      {
        onSuccess: () => {
          if (accountType === 'COORDINATORS' && refetchCoordinatorListing) {
            refetchCoordinatorListing().then();
          }

          if (accountType === 'CANDIDATES' && fetchCandidate) {
            fetchCandidate();
          }

          updateEmailStatus(
              userInfo.index,
              'success',
              t('admin->>UniqueLink.EmailHasSent', {name: userInfo.name})
          );
          setTimeout(() => {
            updateEmailStatus(userInfo.index, '');
          }, 5000);

          onDialogClose();
        },
        onError: (error) => {
          updateEmailStatus(
              userInfo.index,
              'error',
              error.response ? error.response.data.message : error.message
          );
          onDialogClose();
        },
      }
  );

  const [dateOfExpiry, setDateOfExpiry] = useState<null | string>(null);

  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleSendUniqueLinkEmail = (formData: SendUniqueLinkEmailFormData) => {
    sendUniqueLinkEmailMutate({
      isWaiveSubjectAdminFee: false,
      isWaiveLateFee: false,
      isOfflinePayment: null,
      userType: accountType,
      uuid: userInfo.uuid,
      examLevelUuid,
      applicationType,
      // exemptionRuleUuids: exemptionRulesValues.map((v) => v.value),
      // subjects: subjectPayloadForSendUniqueLink,
      expiryDate: formData.expiryDate,
      description: formData.description,
    });
  };

  return (
      <Dialog
          id="unique-link-grant-access"
          open={modalState}
          maxWidth="lg"
          onClose={onDialogClose}
          className={classes.viewDetailsDialog}
      >
        <DialogTitle>{t('admin->>UniqueLink.GrantAccess')}</DialogTitle>

        <form onSubmit={handleSubmit(handleSendUniqueLinkEmail)}>
          <DialogContent>
            <Box className={classes.dialogContentContainer}>
              <Typography>{t('admin->>UniqueLink.GrantAccessSelectType')}</Typography>
              {(
                  <Box className={classes.selectContainer}>
                    <ControlledDatePicker
                        label={t('admin->>UniqueLink.ExpiryDateLabel')}
                        value={dateOfExpiry}
                        id="input-text-expiryDate"
                        // @ts-ignore
                        onChange={(date) => {
                          const value = date || '';
                          setDateOfExpiry(value);
                          setValue('expiryDate', value, {shouldValidate: true});
                        }}
                        control={control}
                        name="expiryDate"
                        disablePast
                        isRequired
                        error={!!errors.expiryDate}
                        helperText={errors?.expiryDate?.message?.toString()}
                        inputVariant="filled"
                    />
                    <WordCountArea
                        className={classes.input}
                        label={t('admin->>UniqueLink.DescriptionLabel')}
                        placeholder={t('admin->>UniqueLink.DescriptionPlaceholder')}
                        maxLength={200}
                        isRequired
                        minRows={3}
                        error={!!errors?.description}
                        form={formHook}
                        name="description"
                        // CPEPOKPNEH-3391
                        onChange={(e) => {
                            const trimmedValue = e.target.value.slice(0, 200); // enforce maxLength
                            formHook.setValue('description', trimmedValue);
                        }}
                    />
                  </Box>
              )}
            </Box>
          </DialogContent>
          {errorMessage && (
              <Typography className={classes.errorText} variant="body2">
                {errorMessage}
              </Typography>
          )}
          <DialogActions>
            <Button type="button" variant="text" onClick={onDialogClose}>
              {t('admin->>UniqueLink.Cancel')}
            </Button>

            <ApiButton type="submit" status={sendEmailStatus}>
              {t('admin->>UniqueLink.SendEmail')}
            </ApiButton>
          </DialogActions>
        </form>
      </Dialog>
  );
};
export default GrantAccessModal;
